import { GraphQLFieldResolver } from 'graphql'
import { Context } from '../../shared'
import { Application } from '../../types/application'
import { mapApplicationFilters } from '../../endpoint-helpers'
import { findByIDAndOrganizationID } from '../../queries/helpers/application'
import { GenericMutationResponse } from '../../types/generic-mutation-response'
import { EntitiesMutationArgs } from '../../types/entities-mutation-args'
import { SingularOrganizationEntityMutationArgs } from '../../types/singular-organization-entity-mutation-args'
import { ApplicationFilterMutationArgs } from '../../types/application-filter-mutation-args'
import { ApplicationFilterArg } from '../../types/application-filter-arg'
import { Connector } from '../../../../api/connector'

type ApplicationMutationType =
  | 'archive'
  | 'restore'
  | 'reset'
  | 'applyLicense'
  | 'delete'
type ApplicationBulkMutationType = 'archive' | 'restore' | 'reset' | 'delete'

function mutateSingular(
  mutationType: ApplicationMutationType,
  args: SingularOrganizationEntityMutationArgs,
  organizationID: string,
  connector: Connector
) {
  const { input } = args
  if (mutationType === 'delete') {
    return connector.executeDelete(
      `/${organizationID}/applications/${input.id}`
    )
  }
  if (mutationType === 'applyLicense') {
    return connector.post(`/${input.organizationID}/licenses/${input.id}`)
  }
  return connector.put(
    `/${organizationID}/applications/${input.id}/${mutationType}`
  )
}

export function applicationMutationResolver(
  mutationType: ApplicationMutationType
): GraphQLFieldResolver<any, Context, SingularOrganizationEntityMutationArgs> {
  return async function toggleArchiveAndGetApplication(
    _source,
    args: SingularOrganizationEntityMutationArgs,
    context: Context
  ): Promise<Application | GenericMutationResponse> {
    const {
      input: { organizationID, id }
    } = args
    const { connector } = context
    await mutateSingular(mutationType, args, organizationID, connector)

    if (mutationType === 'delete') {
      return { _placeholder: true }
    }
    return findByIDAndOrganizationID(id, organizationID, connector)
  }
}

function mutate(
  mutationType: ApplicationMutationType,
  applicationID: string,
  organizationID: string,
  connector: Connector
) {
  return connector.put(
    `/${organizationID}/applications/${applicationID}/${mutationType}`
  )
}

function mutateMultiple(
  applicationIDs: string[],
  organizationID: string,
  connector: Connector
) {
  return connector.executeDelete(`/${organizationID}/applications`, {
    applications: applicationIDs
  })
}

export function applicationsMutationResolver(
  mutationType: ApplicationBulkMutationType
): GraphQLFieldResolver<any, Context, EntitiesMutationArgs> {
  return async function mutateAndGetApplications(
    _source,
    args,
    context
  ): Promise<GenericMutationResponse> {
    const { input } = args

    if (!input) {
      throw new Error('mutation input parameter is required')
    }

    const { ids, organizationID } = input

    const { connector } = context

    // eslint-disable-next-line unicorn/prefer-ternary
    if (mutationType === 'delete') {
      await mutateMultiple(ids, organizationID, connector)
    } else {
      await Promise.all(
        ids.map((applicationID) =>
          mutate(mutationType, applicationID, organizationID, connector)
        )
      )
    }

    return { _placeholder: true }
  }
}

function mutateWithFilters(
  mutationType: ApplicationMutationType,
  filters: ApplicationFilterArg,
  excludedApplications: string[],
  organizationID: string,
  connector: Connector
) {
  if (mutationType === 'delete') {
    return connector.executeDelete(
      `/${organizationID}/applications/bulk/${mutationType}`,
      {
        ...mapApplicationFilters(filters),
        excludedApplications
      }
    )
  }

  return connector.put(`/${organizationID}/applications/bulk/${mutationType}`, {
    ...mapApplicationFilters(filters),
    excludedApplications
  })
}

export function applicationsFilterMutationResolver(
  mutationType: ApplicationBulkMutationType
): GraphQLFieldResolver<any, Context, ApplicationFilterMutationArgs> {
  return async function mutate(
    _source,
    args,
    context
  ): Promise<GenericMutationResponse> {
    const { input } = args

    if (!input) {
      throw new Error('mutation input parameter is required')
    }

    const {
      input: { organizationID, filters, excludedApplications }
    } = args
    const { connector } = context

    await mutateWithFilters(
      mutationType,
      filters,
      excludedApplications,
      organizationID,
      connector
    )

    return { _placeholder: true }
  }
}
